// Shared HTTP client for new network code: one auth-header builder, one error type, and no
// logging of headers, tokens or request bodies. Old services move over only when a branch touches
// them. src/services/noTokenLogging.test.ts guards the "no logging" rule for every service file.
import { getAuth } from 'firebase/auth';

/** A failed API call. `status` 0 means the request never got an answer (network failure). */
export class ApiError extends Error {
  readonly status: number;
  /** The parsed JSON body, else the raw text, else null. */
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    // Keeps `instanceof ApiError` working whatever the compile target does to Error subclasses.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** True for the error fetch() raises when an AbortController cancels the request. */
export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

const NETWORK_MESSAGE = 'Network error: could not reach the API';
const MAX_MESSAGE_LENGTH = 300;

/**
 * Headers for an authenticated JSON call. Throws ApiError(401) when nobody is signed in or the ID
 * token cannot be obtained. Never logs anything: the token is a credential.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const user = getAuth().currentUser;
  if (!user) throw new ApiError(401, 'You are signed out. Sign in again.');
  let token: string;
  try {
    token = await user.getIdToken();
  } catch (error) {
    // Firebase could not reach its token endpoint: that is a network failure, not a sign-out.
    if ((error as { code?: unknown } | null)?.code === 'auth/network-request-failed') {
      throw new ApiError(0, NETWORK_MESSAGE);
    }
    throw new ApiError(401, 'Your session could not be verified. Sign in again.');
  }
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function buildUrl(baseUrl: string, path: string, query?: Record<string, string | number | undefined>): string {
  const base = baseUrl.replace(/\/+$/, '');
  const tail = path.startsWith('/') ? path : `/${path}`;
  const params = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.append(key, String(value));
    }
  }
  const qs = params.toString();
  if (!qs) return `${base}${tail}`;
  return `${base}${tail}${tail.includes('?') ? '&' : '?'}${qs}`;
}

/** The message a JSON error body carries: `error`, `error.message` or `message`; else null. */
function messageFromBody(body: unknown): string | null {
  if (body === null || typeof body !== 'object') return null;
  const b = body as { error?: unknown; message?: unknown };
  if (typeof b.error === 'string' && b.error.trim()) return b.error.trim().slice(0, MAX_MESSAGE_LENGTH);
  const nested = (b.error as { message?: unknown } | null | undefined)?.message;
  if (typeof nested === 'string' && nested.trim()) return nested.trim().slice(0, MAX_MESSAGE_LENGTH);
  if (typeof b.message === 'string' && b.message.trim()) return b.message.trim().slice(0, MAX_MESSAGE_LENGTH);
  return null;
}

function errorMessage(status: number, statusText: string, text: string, body: unknown): string {
  const fromBody = messageFromBody(body);
  if (fromBody) return fromBody;
  const trimmed = text.trim();
  // A gateway's HTML error page is not a message a person can use.
  if (trimmed && !trimmed.startsWith('<')) return trimmed.slice(0, MAX_MESSAGE_LENGTH);
  return statusText || `HTTP ${status}`;
}

/** An answer read as text, whatever its status. */
interface TextAnswer {
  status: number;
  ok: boolean;
  statusText: string;
  text: string;
}

async function fetchText(url: string, init: RequestInit): Promise<TextAnswer> {
  try {
    const res = await fetch(url, init);
    return { status: res.status, ok: res.ok, statusText: res.statusText, text: await res.text() };
  } catch (error) {
    if (isAbortError(error)) throw error;
    if (error instanceof TypeError) throw new ApiError(0, NETWORK_MESSAGE);
    throw error;
  }
}

async function send<T>(url: string, init: RequestInit): Promise<T> {
  const { status, ok, statusText, text } = await fetchText(url, init);

  let parsed = false;
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
      parsed = true;
    } catch {
      parsed = false;
    }
  }

  if (!ok) {
    throw new ApiError(status, errorMessage(status, statusText, text, json), parsed ? json : text || null);
  }
  if (!text) return undefined as T; // 204 and other empty successes
  if (!parsed) throw new ApiError(status, 'The API sent a response that is not valid JSON', text);
  return json as T;
}

/** GET `${baseUrl}${path}?query` as JSON. Undefined query values are skipped. */
export async function getJson<T>(
  baseUrl: string,
  path: string,
  o?: { query?: Record<string, string | number | undefined>; signal?: AbortSignal },
): Promise<T> {
  const headers = await authHeaders();
  return send<T>(buildUrl(baseUrl, path, o?.query), { method: 'GET', headers, signal: o?.signal });
}

/** GET `${baseUrl}${path}` as text (a CSV), with the same auth and the same errors as getJson. */
export async function getText(baseUrl: string, path: string, o?: { signal?: AbortSignal }): Promise<string> {
  const headers = await authHeaders();
  const answer = await fetchText(buildUrl(baseUrl, path), { method: 'GET', headers, signal: o?.signal });
  if (answer.ok) return answer.text;
  let body: unknown = null;
  try {
    body = JSON.parse(answer.text);
  } catch {
    body = null;
  }
  throw new ApiError(answer.status, errorMessage(answer.status, answer.statusText, answer.text, body), body ?? (answer.text || null));
}

/**
 * GET `${baseUrl}${path}?query` as JSON with no Authorization header, for a public API (the demo
 * API). Same errors as getJson. Never sends a token, so it must not be pointed at the data API.
 */
export async function getPublicJson<T>(
  baseUrl: string,
  path: string,
  o?: { query?: Record<string, string | number | undefined>; signal?: AbortSignal },
): Promise<T> {
  return send<T>(buildUrl(baseUrl, path, o?.query), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: o?.signal,
  });
}

/** POST `body` as JSON to `${baseUrl}${path}` and parse the JSON answer. */
export async function postJson<T>(
  baseUrl: string,
  path: string,
  body: unknown,
  o?: { signal?: AbortSignal },
): Promise<T> {
  const headers = await authHeaders();
  return send<T>(buildUrl(baseUrl, path), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: o?.signal,
  });
}

/**
 * Text for a failed job. The backend sends the error as a string, as `{ message }` or as
 * `{ reason }`, and rendering an object as a React child crashes the page.
 */
export function describeJobError(error: unknown): string {
  if (typeof error === 'string' && error.trim()) return error;
  if (error !== null && typeof error === 'object') {
    const e = error as { message?: unknown; reason?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    if (typeof e.reason === 'string' && e.reason.trim()) return e.reason;
  }
  return 'Job failed';
}

// The older services (activities, ODM) still throw `new Error('API Error 400: <body>')` and
// 'Network error - check CORS ...'. This reads them the way ApiError reads a response.
const LEGACY_API_ERROR = /^API Error (\d{3}):\s*([\s\S]*)$/;

/**
 * Text for a failed request that a person can read: an ApiError's message, the `error` field of
 * a JSON body the older services pasted after "API Error 400:", or the message of any other
 * Error. Never a raw JSON dump.
 */
export function describeRequestError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) {
    const message = error.message.trim();
    if (/^Network error/i.test(message)) return NETWORK_MESSAGE;
    const legacy = LEGACY_API_ERROR.exec(message);
    if (legacy) {
      const status = Number(legacy[1]);
      const text = legacy[2].trim();
      let body: unknown = null;
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
      // A JSON body with no message in it is not shown as text.
      const plain = body !== null && typeof body === 'object' ? '' : text;
      return errorMessage(
        status,
        status >= 500 ? 'The server had a problem. Try again in a moment.' : 'The request was not accepted.',
        plain,
        body,
      );
    }
    if (message) return message;
  }
  if (typeof error === 'string' && error.trim()) return error.trim();
  return 'Something went wrong. Try again.';
}
